/**
 * Pure ordering helpers shared by the server mutators (mutators.ts), the
 * optimistic client mutators (client-mutators.ts) and the menu editor UI.
 * Both mutator sides must compute byte-identical positions, so ties are
 * always broken by id (client-chosen and identical on both sides) rather
 * than by global_sequence (which differs between the optimistic row and the
 * server row).
 */

export type Positioned = { id: string; position: number };
export type MoveDirection = 'up' | 'down';
export type PositionWrite = { id: string; position: number };

/** Display order: position ascending, then id. Never mutates `rows`. */
export function sortByPosition<T extends Positioned>(rows: readonly T[]): T[] {
  return [...rows].sort(
    (a, b) =>
      Number(a.position) - Number(b.position) ||
      (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  );
}

/** Position for a row appended after `rows` (0 for an empty list). */
export function nextPosition(
  rows: readonly { position: number | null | undefined }[]
): number {
  let max = -1;
  for (const row of rows) {
    const position = Number(row.position ?? 0);
    if (Number.isFinite(position) && position > max) max = position;
  }
  return max + 1;
}

/**
 * Moves `id` one step up or down and returns the position writes needed,
 * renumbering the whole list to 0..n-1 so later moves never meet ties.
 * Returns `[]` (a no-op) when `id` is not in `rows` or is already first
 * (up) / last (down).
 */
export function moveInOrder<T extends Positioned>(
  rows: readonly T[],
  id: string,
  direction: MoveDirection
): PositionWrite[] {
  const ordered = sortByPosition(rows);
  const index = ordered.findIndex(row => row.id === id);
  if (index === -1) return [];
  const target = direction === 'up' ? index - 1 : index + 1;
  if (target < 0 || target >= ordered.length) return [];
  const next = [...ordered];
  [next[index], next[target]] = [next[target], next[index]];
  return next.flatMap((row, position) =>
    Number(row.position) === position ? [] : [{ id: row.id, position }]
  );
}
